import { countries } from 'boot/countries';
import { filterPropertyTypes } from 'boot/globals';
import { Timestamp } from 'firebase/firestore';
import { chain, concat, find, get, head, isArray, isEmpty, map } from 'lodash';
import { mapState } from 'pinia';
import Countries from 'src/mixins/Countries';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Integrations from 'src/mixins/Integrations';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { units } from 'src/store/units';
import { calculateAge } from 'src/utils/DateUtils';
import {
  STAFF_LICENSE_STATUS,
  formatLicenseExpirationDate,
  getStaffLicenseStatusLabelKey,
  getStaffLicenseStatusValue,
} from 'src/utils/staffLicenseType';

export default {

  mixins: [
    Countries,
    Integrations,
    FiltersMixin,
    Toolbox,
    Time,
  ],

  computed: {
    ...mapState(auth, ['connectedClinic', 'isManagerOrAdministrator', 'careModelCohorts']),
    ...mapState(units, ['units', 'userUnits']),
    ...mapState(clients, ['clients']),
    ...mapState(dataFields, ['clientDataFields', 'allStaffMemberDataFields']),

    clientColumns() {
      const columns = [
        {
          name: 'clientName',
          required: true,
          label: this.$t('clients.columnsClientNameLabel'),
          align: 'left',
          field: 'clientName',
          type: 'text',
          sortable: true,
          mandatory: true,
          profileTypes: ['client'],
        },
        {
          name: 'clientName',
          label: this.$t('clients.columnsStaffMemberNameLabel'),
          align: 'left',
          field: 'clientName',
          type: 'text',
          sortable: true,
          profileTypes: ['staffMember'],
        },
        {
          name: 'firstName',
          required: true,
          label: this.$t('clients.columnsFirstNameLabel'),
          align: 'left',
          field: 'firstName',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],
        },
        {
          name: 'lastName',
          required: true,
          label: this.$t('clients.columnsLastNameLabel'),
          align: 'left',
          field: 'lastName',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],
        },
        {
          name: 'alias',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientAliasLabel'),
          align: 'left',
          field: 'alias',
          type: 'text',
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'alias',
        },
        {
          name: 'reference',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientReferenceIDLabel'),
          align: 'left',
          field: 'reference',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'reference',
        },
        {
          name: 'gender',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientGenderLabel'),
          align: 'left',
          field: 'gender',
          type: 'select',
          options: map(this.$tm('clients.modals.editClient.gender'), (genderLabel, genderKey) => ({
            value: genderKey,
            label: genderLabel,
          })),
          format: (gender) => (get(this.$tm('clients.modals.editClient.gender'), gender, '')),
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterProperty: 'gender',
          filterItems: map(this.$tm('clients.modals.editClient.gender'), (genderLabel, genderKey) => ({
            value: genderKey,
            label: genderLabel,
          })),
        },
        {
          name: 'birthDate',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientDOBLabel'),
          align: 'left',
          field: 'birthDate',
          type: 'date',
          format: (birthDate) => ((birthDate instanceof Timestamp) ? this.getLocaleDateString(birthDate) : null),
          sortable: true,
          profileTypes: ['client'],
        },
        {
          name: 'age',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientAgeLabel'),
          align: 'left',
          field: 'birthDate',
          format: (birthDate) => calculateAge(birthDate),
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.Number,
          filterProperty: 'birthDate',
          filterPropertyCallback: (row) => calculateAge(row.birthDate, false),
        },
        {
          name: 'email',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientEmailLabel'),
          align: 'left',
          field: 'email',
          type: 'text',
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'email',
        },
        {
          name: 'phone',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientPhoneLabel'),
          align: 'left',
          field: 'phone',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'phone',
        },
        {
          name: 'address1',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientAddress1Label'),
          align: 'left',
          field: 'address1',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'address1',
        },
        {
          name: 'address2',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientAddress2Label'),
          align: 'left',
          field: 'address2',
          type: 'text',
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'address2',
        },
        {
          name: 'postalCode',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientPostalCodeLabel'),
          align: 'left',
          field: 'postalCode',
          type: null,
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'postalCode',
        },
        {
          name: 'city',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientCityLabel'),
          align: 'left',
          field: 'city',
          type: null,
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'city',
        },
        {
          name: 'state',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientStateLabel'),
          align: 'left',
          field: 'state',
          type: null,
          format: (state, client) => chain(this.countryStates(get(client, 'country', null)))
            .find({ code: state })
            .get('label', '')
            .defaultTo(state || '')
            .value(),
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: this.stateFilterItems,
        },
        {
          name: 'country',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientCountryLabel'),
          align: 'left',
          field: 'country',
          type: null,
          format: (country) => (country ? this.$t(country) : ''),
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: this.countryFilterItems,
        },
        {
          name: 'units',
          align: 'left',
          label: this.$t('settings.units.columnUnitLabel'),
          field: 'units',
          type: null,
          format: (val) => {
            if (isEmpty(val)) {
              return '';
            }

            const unit = find(this.units, { id: head(val) });

            return get(unit, 'unitName', '');
          },
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.ArrayInArray,
          filterItems: this.businessUnitsFilterItems,
          // Stored as an array to leave room for more, but a client is assigned exactly one unit.
          filterSingleValued: true,
        },
        this.careModelCohorts?.length > 0 && {
          name: 'careModelCohort',
          align: 'left',
          label: this.$t('clients.modals.editClient.inputClientCohortLabel'),
          field: 'careModelCohort',
          type: null,
          format: (cohortId) => {
            if (!cohortId) {
              return this.$t('cohorts.unassignedLabel');
            }

            const cohort = find(this.careModelCohorts, { id: cohortId });

            return get(cohort, 'cohortName', '');
          },
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: this.careModelCohortsFilterItems,
        },
        {
          name: 'employeeStartDate',
          align: 'left',
          label: this.$t('staff.modals.editStaffMember.inputClientEmployeeStartDateLabel'),
          field: 'employeeStartDate',
          type: 'date',
          format: (data) => ((data instanceof Timestamp) ? this.getLocaleDateString(data) : null),
          sortable: true,
          profileTypes: ['staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.Date,
          filterProperty: 'employeeStartDate',
        },
        {
          name: 'employeeEndDate',
          align: 'left',
          label: this.$t('staff.modals.editStaffMember.inputClientEmployeeEndDateLabel'),
          field: 'employeeEndDate',
          type: 'date',
          format: (data) => ((data instanceof Timestamp) ? this.getLocaleDateString(data) : null),
          sortable: true,
          profileTypes: ['staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.Date,
          filterProperty: 'employeeEndDate',
        },
        {
          name: 'status',
          align: 'left',
          // @todo this.personType is not defined in this mixin, so this relies on the component
          // using the mixin to define it. This should be refactored to be more robust.
          label: this.personType === 'clients'
            ? this.$t('clients.columnsStatusLabel')
            : this.$t('staff.modals.editStaffMember.columnsLicenseTypeLabel'),
          format: (status, staffMember) => (this.personType === 'staffMember'
            ? this.$t(getStaffLicenseStatusLabelKey(getStaffLicenseStatusValue(staffMember)))
            : this.$tm('clients.status')?.[status] ?? '?'),
          field: 'status',
          sortable: true,
          profileTypes: ['client', 'staffMember'],

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterProperty: 'status',
          ...(this.personType === 'staffMember' ? {
            filterProperty: 'licenseStatusValue',
            filterItems: [
              { value: 'standard', label: this.$t('staff.status.inactive') },
              { value: 'premium', label: this.$t('staff.status.active') },
              { value: 'premiumFree', label: this.$t('staff.status.premiumFree') },
            ],
          } : {
            filterItems: Object.entries(this.$tm('clients.status')).map(([statusKey, statusLabel]) => ({
              value: statusKey,
              label: statusLabel,
            })),
          }),
        },
        ...(this.personType === 'staffMember' ? [{
          name: 'licenseFreeUntil',
          align: 'left',
          label: this.$t('staff.modals.editStaffMember.columnsLicenseExpirationDateLabel'),
          field: 'licenseFreeUntil',
          format: (licenseFreeUntil, staffMember) => (
            getStaffLicenseStatusValue(staffMember) === STAFF_LICENSE_STATUS.PREMIUM_FREE
              ? formatLicenseExpirationDate(licenseFreeUntil)
              : ''
          ),
          profileTypes: ['staffMember'],
          sortable: false,
          filterable: false,
        }] : []),
        {
          name: 'id',
          required: true,
          label: this.$t('clients.modals.editClient.inputClientIdLabel'),
          align: 'left',
          field: 'id',
          sortable: true,
          profileTypes: ['client'],

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'id',
        },

        ...(get(this.connectedClinic, 'integration')
          ? [
            {
              name: 'integration',
              group: 'integration',
              align: 'left',
              label: this.$t(
                'settings.users.columnsIntegrationLabel',
                { integration: this.getIntegrationName(this.connectedClinic.integration) },
              ),
              field: 'integrationReference',
              sortable: true,
              profileTypes: ['client'],

              filterable: true,
              filterType: filterPropertyTypes.NullValue,
              filterItems: [{
                value: false,
                label: this.$t('settings.users.labelIntegrationConnected'),
              },
              {
                value: true,
                label: this.$t('settings.users.labelIntegrationNotConnected'),
              }],
            },
            {
              name: 'integrationId',
              group: 'integration',
              align: 'left',
              label: this.$t(
                'settings.users.columnsIntegrationIdLabel',
                { integration: this.getIntegrationName(this.connectedClinic.integration) },
              ),
              field: 'integrationReference',
              sortable: true,
              profileTypes: ['client'],

              filterable: true,
              filterType: filterPropertyTypes.StringMatch,
            },
          ]
          : []),
      ];

      return columns;
    }, // clientColumns()

    clientDataColumns() {
      return chain(this.clientDataFields)
        .sortBy('order')
        .map(
          (clientDataField) => ({
            name: clientDataField.id,
            label: clientDataField.label,
            align: 'left',
            field: 'customData',
            format: (customData) => (this.formatCustomDataField(clientDataField, customData)),
            sortable: false,
            mandatory: false,
            profileTypes: ['client'],

            filterable: true,
            filterType: this.getCustomDataFieldFilterType(clientDataField),
            filterProperty: this.getCustomDataFieldFilterProperty(clientDataField),
            ...this.getCustomDataFieldFilterItems(clientDataField),
          }),
        )
        .value();
    }, // clientDataColumns()

    staffMemberDataColumns() {
      return this.allStaffMemberDataFields.map(
        (staffMemberDataField) => ({
          name: staffMemberDataField.id,
          label: staffMemberDataField.label,
          align: 'left',
          field: 'customData',
          format: (customData) => (this.formatCustomDataField(staffMemberDataField, customData)),
          sortable: false,
          mandatory: false,
          profileTypes: ['staffMember'],

          filterable: true,
          filterType: this.getCustomDataFieldFilterType(staffMemberDataField),
          filterProperty: this.getClientDataFieldFilterProperty(staffMemberDataField),
          ...this.getCustomDataFieldFilterItems(staffMemberDataField),
        }),
      );
    }, // staffMemberDataColumns()

    careModelCohortsFilterItems() {
      const retVal = map(
        this.careModelCohorts,
        (cohort) => ({
          value: cohort.id,
          label: cohort.cohortName,
        }),
      );

      return concat(retVal, {
        value: null,
        label: this.$t('cohorts.unassignedLabel'),
      });
    }, // careModelCohortsFilterItems()

    businessUnitsFilterItems() {
      const retVal = map(
        this.userUnits,
        (unit) => ({
          value: unit.id,
          label: unit.unitName,
        }),
      );

      if (this.isManagerOrAdministrator) {
        return concat(retVal, {
          value: null,
          label: this.$t('settings.units.noUnitsLabel'),
        });
      }
      return retVal;
    }, // businessUnitsFilterItems()

    countryFilterItems() {
      const filterItemsList = chain(this.clients)
        .map((client) => (client.country?.toUpperCase()))
        .uniq()
        .value();
      const alpha3Codes = countries.getAlpha3Codes();
      return chain(alpha3Codes)
        .pickBy((value) => filterItemsList.includes(value))
        .map((code) => ({
          label: this.normalizeCountryLabel(this.$t(code)),
          value: code,
          flagIcon: code,
        }))
        .sortBy('label')
        .value();
    }, // countryFilterItems()

    stateFilterItems() {
      return chain(this.clients)
        .filter((client) => client.state)
        .map((client) => {
          const stateLabel = chain(this.countryStates(client.country))
            .find({ code: client.state })
            .get('label')
            .value();
          if (!stateLabel) {
            return;
          }
          return {
            value: client.state,
            label: stateLabel,
          };
        })
        .compact()
        .uniqBy('value')
        .sortBy('label')
        .value();
    }, // stateFilterItems()

    lastSessionColumns() {
      return [
        {
          name: 'lastSessionDate',
          group: 'lastSession',
          label: this.$t('clients.columnsLastSessionDateLabel'),
          align: 'left',
          field: (row) => get(row, 'lastSession.runTimestamp'),
          sortable: true,
          format: (runTimestamp) => (runTimestamp instanceof Timestamp
            ? this.getLocaleTimeString(runTimestamp, {
              options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
            }) : ''),
          profileTypes: ['client'],
          filterable: true,
          filterType: filterPropertyTypes.Date,
          filterProperty: 'lastSession.runTimestamp',
          filterDateOperators: true,
        },
        {
          name: 'lastSessionName',
          group: 'lastSession',
          label: this.$t('clients.columnsLastSessionNameLabel'),
          align: 'left',
          field: (row) => get(row, 'lastSession.sessionName'),
          sortable: true,
          format: (sessionName) => sessionName || '',
          profileTypes: ['client'],
          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'lastSession.sessionName',
        },
      ];
    }, // lastSessionColumns()

  },

  methods: {

    normalizeCountryLabel(label) {
      return isArray(label) ? label.join(', ') : label;
    }, // normalizeCountryLabel()

  },

};
